import { useEffect, useRef } from 'react';
import './FloatingCubes.css';

export const FloatingCubes = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let animationFrameId: number;
    let time = 0;

    // Configuration
    const particleCountX = 100;
    const particleCountZ = 50; // Depth
    const spacing = 40;
    const fieldY = 100; // Base height offset

    const resize = () => {
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width;
      canvas.height = height;
    };

    const draw = () => {
      // Clear with a slight trail effect or transparent
      ctx.clearRect(0, 0, width, height);
      
      // We can add a subtle background tint if desired, but let's keep it transparent to overlay on existing bg
      
      const themePrimary = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim();
      // Parse HSL from e.g., "287 65% 41%"
      const [h, s, l] = themePrimary.split(' ').map((v) => parseFloat(v));
      
      ctx.fillStyle = `hsla(${h}, ${s}%, ${l}%, 0.5)`;

      const centerX = width / 2;
      const centerY = height * 0.8; // Move lower down the screen
      
      const fov = 300; // Field of view

      for (let z = 0; z < particleCountZ; z++) {
        for (let x = 0; x < particleCountX; x++) {
          // Grid coordinates centered
          const gx = (x - particleCountX / 2) * spacing;
          const gz = z * spacing - 500; // Push back

          // Wave function for height (Y)
          // Combine multiple sine waves for "terrain" look
          const oscillation = 
            Math.sin(x * 0.2 + time * 0.002) * 50 + 
            Math.sin(z * 0.2 + time * 0.002) * 50;
            
          const gy = oscillation + fieldY; // Adjusted Y offset

          // 3D Projection
          const scale = fov / (fov + gz + 1000); // Simple perspective
          const x2d = gx * scale + centerX;
          const y2d = gy * scale + centerY;

          if (scale > 0) { // Only draw what's in front
            const size = 3 * scale;
            const alpha = (gz + 1000) / 2000; // Fade in distance

            // Color gradient based on height
            // High points = lighter/cyan/pink, Low points = darker/purple
            
            // Hue variation
            const hue = h + oscillation * 0.5; 
            
            ctx.beginPath();
            ctx.arc(x2d, y2d, Math.max(0, size), 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${hue}, ${s}%, ${l + 20}%, ${Math.min(1, alpha)})`;
            ctx.fill();
            
            // Optional: Draw subtle connections (lines) between neighbors for "wireframe" look?
            // The image has mostly dots, so let's stick to dots to keep performance high and look clean.
            // Maybe just glowing dots.
            
            if (scale > 0.5) {
               ctx.shadowBlur = 10;
               ctx.shadowColor = `hsla(${hue}, ${s}%, ${l}%, 1)`;
            } else {
               ctx.shadowBlur = 0;
            }
          }
        }
      }

      time += 10; // Speed
      animationFrameId = requestAnimationFrame(draw);
    };

    window.addEventListener('resize', resize);
    resize();
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="absolute inset-0 pointer-events-none z-0" style={{ perspective: '1000px' }}>
      <canvas ref={canvasRef} className="floating-cubes-canvas" />
    </div>
  );
};
